import React, { FunctionComponent } from 'react'
import { addMoneys } from '@afterpay/utils/lib/money'
import { Divider, Grid, Heading, Text, usePercent } from '@afterpaytouch/core'
import { Money } from '../Money'
import { MoneyPrefix } from '../Money/types'
import { useCheckoutSizes } from '../../utils/responsive'
import { useTranslation } from '../../utils/convergence'
import { useAppRouter, useIsAuPayMonthly, useIsConsumerLendingOrder, useSelectedPaymentOption } from '../../state'
import styles from './style.module.css'

/**
 * "Summary" section of the redesigned Pay Monthly summary page (CASHDAP-3071, project: Dynamic
 * checkout unification 2026). Groups the regulated cost-of-credit disclosure into a dedicated
 * Summary block — purchase amount, interest (with APR), total amount payable, and amount due today
 * — matching the Pay in 4 / Bronte breakdown treatment. Copy + ordering per the FEB review slides
 * and Figma SOT frame 726:20432.
 *
 * Replaces the inline APR/interest/total list previously rendered by `ConsumerLendingPlanDetails`;
 * used only by the redesigned page (`summary-pay-monthly-redesign`). All figures come from the
 * selected `PaymentOption`.
 */
export const ConsumerLendingSummaryBreakdown: FunctionComponent = () => {
  const { t } = useTranslation()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const selectedPaymentOption = useSelectedPaymentOption()
  const isConsumerLending = useIsConsumerLendingOrder()
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const isAuPayMonthly = useIsAuPayMonthly()

  if (selectedPaymentOption === undefined || !isConsumerLending) {
    return null
  }

  const { principalAmount, interestAmount, totalAmount, amountDueToday, apr, repaymentAmount } = selectedPaymentOption

  // Purchase amount = the full order total (financed principal + down payment), NOT the financed
  // amount alone — the amount financed is disclosed in the loan agreement / TIL instead, and the
  // rows must reconcile: purchase + interest = total amount payable (CASHDAP-3901).
  const purchaseAmount = { ...principalAmount, amount: addMoneys({ moneys: [principalAmount, amountDueToday] }) }

  // AU Pay Monthly (rocketship.au-pay-monthly): show the down payment as its own deduction and make the
  // total the backend's repaymentAmount (what is paid later, excluding the down payment) so the rows
  // reconcile: purchase − down payment + interest = total pay later.
  const hasDownPayment = parseFloat(amountDueToday.amount) > 0
  const showDownPaymentRow = isAuPayMonthly && hasDownPayment
  const totalRowAmount = isAuPayMonthly && repaymentAmount !== null ? repaymentAmount : totalAmount

  return (
    <>
      <div className={styles.heading}>
        <Heading size={sectionHeadingSize} renderAs='h2'>
          {t('consumerLending:summary:breakdownTitle')}
        </Heading>
      </div>
      {/* Cost-of-credit lines (purchase + interest), grouped tight. The wrapping div breaks the
          design-system `.grid + .grid` adjacency so this group is visually separate from the totals
          below (matching the approved design + PI4's divider-separated breakdown). */}
      <div className={styles.amountsGroup}>
        <Grid>
          <Text size={textSize} renderAs='span'>
            {t('consumerLending:summary:purchaseAmount')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-purchaseAmount'>
            <Money value={purchaseAmount} size={textSize} />
          </Grid.Item>
        </Grid>
        {showDownPaymentRow && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {t('consumerLending:summary:downPayment')}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-downPayment'>
              <Money value={amountDueToday} size={textSize} prefix={MoneyPrefix.NEGATIVE} />
            </Grid.Item>
          </Grid>
        )}
        {/* AU design separates the amount financed (purchase − down payment) from the interest charged on it. */}
        {showDownPaymentRow && interestAmount !== null && (
          <div className={styles.groupDivider}>
            <Divider kind='Hairline' margin='No' />
          </div>
        )}
        {interestAmount !== null && (
          <Grid>
            <Text size={textSize} renderAs='span'>
              {t('consumerLending:summary:interestWithApr', { apr: formatPercent(apr ?? 0) })}
            </Text>
            <Grid.Item sm='auto' data-testid='clSummaryBreakdown-interestAmount'>
              <Money value={interestAmount} size={textSize} />
            </Grid.Item>
          </Grid>
        )}
      </div>
      <div className={styles.groupDivider}>
        {/* margin='No' so the only spacing is the wrapper's .groupDivider (Divider defaults to
            'Regular' = 16px each side, which would stack to ~28px). */}
        <Divider kind='Hairline' margin='No' />
      </div>
      {/* Totals (total payable + due today), grouped tight. */}
      <div className={styles.amountsGroup}>
        <Grid>
          <Text size={textSize} renderAs='span'>
            {t('consumerLending:summary:totalAmountPayable')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-totalAmount'>
            <Money value={totalRowAmount} size={textSize} />
          </Grid.Item>
        </Grid>
        <Grid>
          <Text size={textSize} renderAs='span' bold>
            {t('consumerLending:summary:dueToday')}
          </Text>
          <Grid.Item sm='auto' data-testid='clSummaryBreakdown-amountDueToday'>
            <Money value={amountDueToday} size={textSize} bold />
          </Grid.Item>
        </Grid>
      </div>
    </>
  )
}
